/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type SuggestionStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Minimum time the "thinking" indicator stays visible so it never flashes.
 */
export const MIN_THINKING_MS = 500;

interface UseSuggestionPickerArgs {
  fetchSuggestions: () => Promise<string[]>;
}

interface UseSuggestionPickerResult {
  open: boolean;
  status: SuggestionStatus;
  options: string[];
  openAndLoad: () => void;
  reload: () => void;
  close: () => void;
}

/**
 * State machine for the suggestion picker. Drives the
 * idle -> loading -> loaded/error transitions while enforcing a minimum
 * visible "thinking" duration so a fast backend response does not flash.
 *
 * @param args - The async fetcher that returns suggestion strings.
 * @returns Picker state plus open/reload/close actions.
 */
export function useSuggestionPicker({
  fetchSuggestions,
}: UseSuggestionPickerArgs): UseSuggestionPickerResult {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SuggestionStatus>('idle');
  const [options, setOptions] = useState<string[]>([]);
  const requestId = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const load = useCallback(() => {
    const id = ++requestId.current;
    setStatus('loading');
    setOptions([]);
    const startedAt = Date.now();
    fetchSuggestions()
      .then((result) => settle(id, () => { setOptions(result); setStatus('loaded'); }, startedAt))
      .catch((err) => {
        console.error('[listing-suggestions] failed', err);
        settle(id, () => setStatus('error'), startedAt);
      });

    function settle(forId: number, apply: () => void, started: number) {
      const remaining = Math.max(0, MIN_THINKING_MS - (Date.now() - started));
      clearTimer();
      timer.current = setTimeout(() => {
        if (forId === requestId.current) apply();
      }, remaining);
    }
  }, [fetchSuggestions, clearTimer]);

  const openAndLoad = useCallback(() => {
    setOpen(true);
    load();
  }, [load]);

  const close = useCallback(() => {
    requestId.current++;
    clearTimer();
    setOpen(false);
    setStatus('idle');
    setOptions([]);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return { open, status, options, openAndLoad, reload: load, close };
}
